import { Badge } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import { InvoiceDownload } from "@components/Invoice/InvoiceDownload";
import { InvoiceKindType, type MeInvoiceListItem } from "@models/invoice";
import {
    currencyFormatter,
    formatOrderNumber,
    formatUtcCalendarDate,
} from "@utils/formatter";

interface InvoiceCardProps {
    invoice: MeInvoiceListItem;
}

const KIND_TITLES: Record<InvoiceKindType, string> = {
    [InvoiceKindType.None]: "Invoice",
    [InvoiceKindType.Order]: "Order Invoice",
    [InvoiceKindType.Membership]: "Membership Invoice",
};

const formatIssuedDate = (iso: string): string =>
    new Date(iso).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });

// "6/12/26", the mockup's period format, `dayOffset` days after the ISO
// string's calendar date, read in UTC so no local timezone shifts a period
// boundary at midnight UTC to the day before.
const formatPeriodDate = (iso: string, dayOffset = 0): string =>
    formatUtcCalendarDate(iso, { dayOffset, shortYear: true });

// One invoice on the Billing History (#215). PO rulings 2026-09-29: the status
// is Issued or Voided only, there is no card brand, and there is no invoice
// detail page. The only action on either kind is the PDF download (which a
// voided invoice still has); an order invoice names its order, unlinked until
// the customer order detail page exists (see the TODO below).
export const InvoiceCard = ({ invoice }: InvoiceCardProps) => {
    // Truthy, as InvoiceDownload reads it: a payload that drops the field
    // must not badge every invoice Voided.
    const isVoided = Boolean(invoice.voidedAt);

    return (
        <article className="bg-base-100 border border-primary/20 rounded-lg p-4 shadow-sm flex flex-col gap-2">
            <div className="flex items-start justify-between gap-3">
                <h2 className="font-bold text-primary text-lg break-all">
                    {KIND_TITLES[invoice.kind] ?? KIND_TITLES[InvoiceKindType.None]}{" "}
                    {invoice.invoiceNumber}
                </h2>
                <span className="font-bold text-primary text-lg whitespace-nowrap">
                    {currencyFormatter.format(invoice.totalAmount)}
                </span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
                {isVoided ? (
                    <Badge color={ComponentColor.Error} size={ComponentSize.MD}>
                        Voided
                    </Badge>
                ) : (
                    // text-neutral: the light theme's base-content is #fff,
                    // which an outline badge would inherit (see CLAUDE.md).
                    <Badge
                        size={ComponentSize.MD}
                        outline
                        className="text-neutral"
                    >
                        Issued
                    </Badge>
                )}
                <span className="text-neutral">
                    {formatIssuedDate(invoice.issuedAt)}
                </span>
            </div>
            {invoice.order && (
                // TODO(E6-S7): the order number will link to the customer
                // order detail page (#204) — plain text until that page
                // exists, as on OrderCard. The Download button already gives
                // the invoice.
                <p className="text-neutral">
                    <span className="font-bold">Order:</span>{" "}
                    {formatOrderNumber(invoice.order.orderNumber)}
                </p>
            )}
            {invoice.membership && (
                <p className="text-neutral">
                    <span className="font-bold">Period:</span>{" "}
                    {formatPeriodDate(invoice.membership.periodStart)} -{" "}
                    {/* The API's periodEnd is EXCLUSIVE (a March period ends
                        04-01); the mockup shows the inclusive last day, so
                        show the day before it. */}
                    {formatPeriodDate(invoice.membership.periodEnd, -1)}
                </p>
            )}
            <InvoiceDownload
                invoice={{
                    id: invoice.id,
                    invoiceNumber: invoice.invoiceNumber,
                    voidedAt: invoice.voidedAt,
                }}
                variant="customer"
            />
        </article>
    );
};
